/*
 * Copyright Mimic Networks, Inc. 2024.
 */

import { MoreOutlined } from '@ant-design/icons';
import { Modal } from 'antd';
import { useState } from 'react';

import { User } from '@/client';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { Button } from '@/primitives/Button';
import { Container } from '@/primitives/Container';
import { Dropdown, MenuProps } from '@/primitives/Dropdown';
import { Text } from '@/primitives/Text';
import { Title } from '@/primitives/Title';

import { ActionType, Role } from './UserTable';

export type UserActionDropdownParams = {
  user: User;
  currentUser: User;
  onChangeRemoved: (user: User, removed: boolean) => void;
  onChangeUpdated: (user: User, role: Role) => void;
  openUserSubtenantsModal: (user: User) => void;
  navigateToUserDetails: (user: User) => void;
  toggleSubtenantOnly: (user: User) => void;
};

export function UserActionDropdown({
  user,
  currentUser,
  onChangeRemoved,
  onChangeUpdated,
  openUserSubtenantsModal,
  navigateToUserDetails,
  toggleSubtenantOnly,
}: UserActionDropdownParams) {
  const { t } = useMimicTranslation('users');
  const [superadminModalOpen, setSuperadminModalOpen] = useState(false);

  const handleConfirmSuperadmin = () => {
    onChangeUpdated(user, 'superadmin');
    setSuperadminModalOpen(false);
  };

  const handleActionClick: MenuProps['onClick'] = (e) => {
    switch (e.key) {
      case ActionType.VIEW:
        navigateToUserDetails(user);
        break;
      case ActionType.ASSIGN:
        openUserSubtenantsModal(user);
        break;
      case ActionType.DELETE:
        onChangeRemoved(user, !user.removed);
        break;
      case ActionType.MAKE_SUBTENANT_ONLY:
        toggleSubtenantOnly(user);
        break;
      case ActionType.MAKE_TENANT_USER:
        toggleSubtenantOnly(user);
        break;
      case ActionType.MAKE_SUPERADMIN:
        setSuperadminModalOpen(true);
        break;
      case ActionType.MAKE_ADMIN:
        onChangeUpdated(user, 'admin');
        break;
      case ActionType.MAKE_EDITOR:
        onChangeUpdated(user, 'editor');
        break;
      case ActionType.MAKE_VIEWER:
        onChangeUpdated(user, 'viewer');
        break;
      default:
        break;
    }
  };

  const items: MenuProps['items'] = [
    {
      label: t('actions.view'),
      key: ActionType.VIEW,
      'data-testid': 'view-user',
    },
    {
      label: t('actions.makeSuperadmin'),
      key: ActionType.MAKE_SUPERADMIN,
      'data-testid': 'role-option-superadmin',
    },
    {
      label: t('actions.makeAdmin'),
      key: ActionType.MAKE_ADMIN,
      'data-testid': 'role-option-admin',
    },
    {
      label: t('actions.makeEditor'),
      key: ActionType.MAKE_EDITOR,
      'data-testid': 'role-option-editor',
    },
    {
      label: t('actions.makeViewer'),
      key: ActionType.MAKE_VIEWER,
      'data-testid': 'role-option-viewer',
    },
    {
      label: t('actions.makeSubtenantOnly'),
      key: ActionType.MAKE_SUBTENANT_ONLY,
      'data-testid': 'role-option-subtenant-only',
    },
    {
      label: t('actions.makeTenantUser'),
      key: ActionType.MAKE_TENANT_USER,
      'data-testid': 'role-option-tenant-user',
    },
    {
      label: t('actions.assignToSubtenant'),
      key: ActionType.ASSIGN,
      'data-testid': 'assign-user',
    },
    {
      label: t('actions.restore'),
      key: ActionType.DELETE,
      'data-testid': 'delete-user',
    },
  ].filter((item) => {
    if (item.key === ActionType.MAKE_SUBTENANT_ONLY && user.subtenantOnly) {
      return false;
    }
    if (item.key === ActionType.MAKE_TENANT_USER && !user.subtenantOnly) {
      return false;
    }
    if (item.key === ActionType.DELETE && !user.removed) {
      return false;
    }
    if (
      item.key === ActionType.MAKE_SUPERADMIN &&
      (currentUser.role !== 'superadmin' ||
        user.role === 'superadmin' ||
        !user.email?.toLowerCase().endsWith('@mimic.com'))
    ) {
      return false;
    }
    if (item.key === ActionType.MAKE_ADMIN && user.role === 'admin') {
      return false;
    }
    if (item.key === ActionType.MAKE_EDITOR && user.role === 'editor') {
      return false;
    }
    if (item.key === ActionType.MAKE_VIEWER && user.role === 'viewer') {
      return false;
    }

    return true;
  });

  const menu = {
    items,
    onClick: handleActionClick,
  };

  if (user.id !== currentUser.id) {
    return (
      <>
        <Dropdown menu={menu}>
          <Button type="text" dataTestId={`user-options-${user.id}`}>
            <MoreOutlined />
          </Button>
        </Dropdown>
        <Modal
          open={superadminModalOpen}
          onCancel={() => setSuperadminModalOpen(false)}
          onOk={handleConfirmSuperadmin}
          cancelText={t('superadminConfirm.cancel')}
          okText={t('superadminConfirm.confirm')}
          okButtonProps={{ danger: true }}
        >
          <Container padding="sm">
            <Title level={4}>{t('superadminConfirm.title')}</Title>
            <Text>{t('superadminConfirm.description', { userName: user.displayName })}</Text>
          </Container>
        </Modal>
      </>
    );
  }
}
